import { useState, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import type { Account, AccountRole, DirectoryAccount } from '@workflow/contracts'
import {
  Avatar,
  Button,
  ConfirmButton,
  Dialog,
  Dropdown,
  ENTER,
  EmptyNote,
  Icons,
  Loading,
  Segmented,
  StateNote,
  SwitchPanel,
  Tabs,
  staggerDelay,
  StatusLabel,
  cn,
} from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { personName, realName } from '@/lib/people'
import {
  useApproveMember,
  useChangeMemberRole,
  useLevels,
  useSetMemberLevel,
  useDeleteMember,
  useMember,
  useMembers,
  useRejectMember,
  useRestoreMember,
  useRevokeMember,
  useSession,
  useSuspendMember,
} from '@/lib/api/hooks'
import { SettingsRow } from '@/shell/Rows'
import { ACCOUNT_STATUS } from './accountStatus'
import { ROLE_LABEL } from './roles'
import { ProfileReview } from './ProfileReview'
import { MemberWork } from './MemberWork'
import { ProfileForm } from '@/features/account/ProfileForm'
import { JobNoteComposer, JobNotes } from '@/features/jobs/JobNotes'
import { LevelGlyph, LevelMark } from '@/features/levels/LevelMark'

type DirectoryFilter = 'all' | 'player' | 'staff' | 'admin'

/** How many rows a group shows before it folds the rest behind Show All. */
export const GROUP_PREVIEW = 8

const INACTIVE: readonly DirectoryAccount['status'][] = ['suspended', 'revoked', 'rejected']

/** The directory's groups, in reading order: who runs the studio, who works in it, and who has left it. */
export function directoryGroups(members: readonly DirectoryAccount[], filter: DirectoryFilter, query: string) {
  const needle = query.trim().toLowerCase()
  const matches = (member: DirectoryAccount) =>
    !needle || [member.name, member.discordName, member.email].some((value) => value?.toLowerCase().includes(needle))
  const shown = members.filter((member) => matches(member) && (filter === 'all' || member.role === filter))
  const byName = (a: DirectoryAccount, b: DirectoryAccount) => personName(a).localeCompare(personName(b))
  const live = shown.filter((member) => !INACTIVE.includes(member.status))
  return [
    { key: 'admin', title: 'Administrators', people: live.filter((member) => member.role === 'admin').sort(byName) },
    { key: 'staff', title: 'Staff', people: live.filter((member) => member.role === 'staff').sort(byName) },
    { key: 'player', title: 'Players', people: live.filter((member) => member.role === 'player').sort(byName) },
    { key: 'inactive', title: 'Inactive', people: shown.filter((member) => INACTIVE.includes(member.status)).sort(byName) },
  ].filter((group) => group.people.length > 0)
}

/**
 * Everyone: staff and players in one directory, because they are one
 * account system — built to stay calm at a hundred people and more. A search
 * box and a row of tabs narrow it; what remains reads in groups (who runs
 * the studio, staff, players, and those no longer active), each showing its
 * first few with a quiet Show All for the rest, so the page never becomes an
 * endless scroll and never needs pages. A search shows every match.
 *
 * Clicking a row raises the person into a floating window, where reading
 * the record and acting on it are the same moment. Self is visible but not
 * controllable; the server refuses an administrator who would act on their
 * own account, and the window simply does not offer it.
 */
export function DirectoryPane() {
  const members = useMembers()
  const session = useSession()
  const meId = session.data?.account?.id ?? ''
  const [openId, setOpenId] = useState<string | null>(null)
  const [filter, setFilter] = useState<DirectoryFilter>('all')
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  if (members.isLoading) return <Loading label="Loading people" />
  if (members.error || !members.data) {
    return (
      <StateNote
        title="The directory did not load."
        detail={describeError(members.error)}
        action={
          <Button size="sm" icon={<Icons.retry />} onClick={() => void members.refetch()}>
            Try Again
          </Button>
        }
      />
    )
  }

  const all = members.data
  const count = (role: DirectoryFilter) => all.filter((member) => role === 'all' || member.role === role).length
  const groups = directoryGroups(all, filter, query)
  const searching = query.trim() !== ''

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <Tabs<DirectoryFilter>
          label="Show"
          value={filter}
          onChange={setFilter}
          className="min-w-0 gap-4 sm:gap-6"
          options={[
            { value: 'all', label: `All ${count('all')}` },
            { value: 'player', label: `Players ${count('player')}` },
            { value: 'staff', label: `Staff ${count('staff')}` },
            { value: 'admin', label: `Admins ${count('admin')}` },
          ]}
        />
        <label className="flex h-9 items-center gap-2 rounded-full bg-tint px-3.5 sm:w-64">
          <span className="text-muted-foreground" aria-hidden><Icons.search size="sm" /></span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name or email"
            aria-label="Search people"
            className="min-w-0 flex-1 bg-transparent text-ui text-foreground outline-none placeholder:text-muted-foreground"
          />
        </label>
      </div>

      {/* A tab hands the list over by the one switching rule: the old list
          dissolves, the new one rises. */}
      <SwitchPanel activeKey={filter}>
        {groups.length === 0 ? (
          <p className="mt-6 text-ui text-muted-foreground">{searching ? `No one matches “${query.trim()}”.` : 'No one here yet.'}</p>
        ) : (
          groups.map((group) => {
            // Inactive accounts start folded entirely; the rest show their first few.
            const open = expanded[group.key] ?? false
            const limit = searching ? group.people.length : group.key === 'inactive' && !open ? 0 : open ? group.people.length : GROUP_PREVIEW
            const rows = group.people.slice(0, limit)
            const hidden = group.people.length - rows.length
            const toggle = () => setExpanded((all) => ({ ...all, [group.key]: !open }))
            return (
              <section key={group.key} className="mt-7">
                <h3 className="border-b border-hairline pb-2 text-ui font-medium text-foreground">
                  {group.title} <span className="ml-1 font-normal tabular-nums text-muted-foreground">{group.people.length}</span>
                </h3>
                {rows.length > 0 ? (
                  <ul className="flex flex-col">
                    {rows.map((member, index) => (
                      <PersonRow
                        key={member.id}
                        member={member}
                        meId={meId}
                        open={openId === member.id}
                        onOpen={() => setOpenId(member.id)}
                        // Rows a Show All brings in rise into place; the first few are already there.
                        rise={index >= GROUP_PREVIEW || group.key === 'inactive' ? index : null}
                      />
                    ))}
                  </ul>
                ) : null}
                {!searching && (hidden > 0 || open) ? (
                  <div className="mt-3 flex justify-center">
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<span className={cn('flex transition-transform duration-fast', open ? '-rotate-90' : 'rotate-90')}><Icons.disclosure size="sm" /></span>}
                      onClick={toggle}
                    >
                      {open ? 'Show Less' : group.key === 'inactive' && rows.length === 0 ? `Show ${group.people.length} Inactive` : `Show All ${group.people.length}`}
                    </Button>
                  </div>
                ) : null}
              </section>
            )
          })
        )}
      </SwitchPanel>

      <Dialog open={openId !== null} onClose={() => setOpenId(null)} className="sm:max-w-2xl xl:max-w-3xl">
        {openId ? <PersonPanel id={openId} meId={meId} onClose={() => setOpenId(null)} /> : null}
      </Dialog>
    </div>
  )
}

/**
 * The invites not yet taken up: every address let in that has not signed in,
 * newest first. A row opens the person's window, where the invitation can be
 * taken back. Inviting itself is the page's own button.
 */
export function InvitesPane() {
  const members = useMembers()
  const session = useSession()
  const meId = session.data?.account?.id ?? ''
  const [openId, setOpenId] = useState<string | null>(null)
  if (members.isLoading) return <Loading label="Loading invites" />
  if (members.error || !members.data) {
    return <StateNote title="The invites did not load." detail={describeError(members.error)} />
  }
  const invited = members.data
    .filter((member) => member.status === 'invited')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  return (
    <div>
      {invited.length === 0 ? (
        <EmptyNote title="No open invites." detail="Invite an address and it waits here until they sign in for the first time." />
      ) : (
        <ul className="flex flex-col">
          {invited.map((member, index) => (
            <PersonRow key={member.id} member={member} meId={meId} open={openId === member.id} onOpen={() => setOpenId(member.id)} rise={index} />
          ))}
        </ul>
      )}
      <Dialog open={openId !== null} onClose={() => setOpenId(null)} className="sm:max-w-2xl xl:max-w-3xl">
        {openId ? <PersonPanel id={openId} meId={meId} onClose={() => setOpenId(null)} /> : null}
      </Dialog>
    </div>
  )
}

function PersonRow({
  member,
  meId,
  open,
  onOpen,
  rise = null,
}: {
  member: DirectoryAccount
  meId: string
  open: boolean
  onOpen: () => void
  /** Rise into place, staggered by this index, when the row arrives after the list did. */
  rise?: number | null
}) {
  const isMe = member.id === meId
  const shown = ACCOUNT_STATUS[member.status]
  // The clock reads on the roster: a player's logged-in state at a glance —
  // the assign form's first question.
  const clock =
    member.role === 'player' && member.status === 'active' ? (
      <StatusLabel
        tone={member.availability === 'active' ? 'clocked-in' : 'clocked-out'}
        label={member.availability === 'active' ? 'Logged In' : 'Logged Out'}
      />
    ) : null

  return (
    <motion.li
      initial={rise === null ? false : ENTER.initial}
      animate={{ ...ENTER.animate, transition: { ...ENTER.animate.transition, delay: rise === null ? 0 : staggerDelay(rise % GROUP_PREVIEW) } }}
      className="border-b border-hairline last:border-0"
    >
      <button
        type="button"
        onClick={onOpen}
        className="-mx-2 flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors duration-fast hover:bg-tint"
      >
        <Avatar name={personName(member)} email={member.email} src={member.pictureUrl} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-foreground">
            {personName(member)}
            {isMe ? <span className="font-normal text-muted-foreground"> (you)</span> : null}
            {member.level ? (
              <span className="ml-1.5 inline-flex align-[-2px] text-muted-foreground" title={member.level.name} aria-label={member.level.name}>
                <LevelGlyph icon={member.level.icon} />
              </span>
            ) : null}
          </span>
          {realName(member) ? (
            <span className="block truncate text-caption text-muted-foreground">{realName(member)}</span>
          ) : null}
        </span>
        <span className="hidden w-20 shrink-0 text-caption text-muted-foreground sm:block">
          {ROLE_LABEL[member.role]}
        </span>
        {/* Role, clock and account state sit in fixed slots rendered for every
            row — empty where they do not apply — so the columns land on the
            same lines however long the words run. The clock waits for a wide
            enough row; the person's panel carries it on a small one. */}
        <span className="hidden w-28 shrink-0 sm:block">{clock}</span>
        <span className="w-28 shrink-0">
          <StatusLabel className="whitespace-nowrap" tone={shown.tone} label={shown.label} />
        </span>
        <span
          className={cn(
            'flex shrink-0 items-center text-muted-foreground transition-transform duration-fast',
            open && 'rotate-90',
          )}
        >
          <Icons.disclosure />
        </span>
      </button>
    </motion.li>
  )
}

/**
 * The person, raised out of the page: the record, and beneath it the
 * Manage section where the account's own verbs wait — one decision per row,
 * nothing reachable from the ledger itself.
 */
function PersonPanel({ id, meId, onClose }: { id: string; meId: string; onClose: () => void }) {
  const detail = useMember(id)
  const changeRole = useChangeMemberRole()
  const levels = useLevels()
  const setLevel = useSetMemberLevel()
  // The verbs live here, so a refusal — suspending the last administrator,
  // revoking an address mid-session — says itself here.
  const approve = useApproveMember()
  const reject = useRejectMember()
  const suspend = useSuspendMember()
  const revoke = useRevokeMember()
  const restore = useRestoreMember()
  const removeMember = useDeleteMember()
  const [editingProfile, setEditingProfile] = useState(false)
  if (detail.isLoading) return <PersonFrame onClose={onClose}><Loading label="Loading account" /></PersonFrame>
  if (detail.error || !detail.data) {
    return (
      <PersonFrame onClose={onClose}>
        <StateNote title="This account could not be loaded." detail={describeError(detail.error)} />
      </PersonFrame>
    )
  }
  const member = detail.data
  const isMe = member.id === meId
  const fault = approve.error ?? reject.error ?? suspend.error ?? revoke.error ?? restore.error ?? removeMember.error

  return (
    <PersonFrame onClose={onClose}>
      <div className="flex items-center gap-3 pr-9">
        <Avatar name={personName(member)} email={member.email} src={member.pictureUrl} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-ui font-medium text-foreground">{personName(member)}</p>
          <p className="truncate text-caption text-muted-foreground">
            {member.level ? <><LevelMark level={member.level} className="align-[-2px]" /> · </> : null}
            {[
              realName(member),
              ROLE_LABEL[member.role],
              // The clock reads here too, as words — the panel is the record.
              member.role === 'player' && member.status === 'active'
                ? member.availability === 'active'
                  ? 'Logged In'
                  : 'Logged Out'
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <StatusLabel tone={ACCOUNT_STATUS[member.status].tone} label={ACCOUNT_STATUS[member.status].label} />
      </div>

      {/* The edit affordance lives above the record, not below it — a small
          link after fifteen fields was one nobody found. */}
      {!editingProfile ? (
        <div className="mt-4">
          <Button size="sm" variant="secondary" onClick={() => setEditingProfile(true)}>
            Edit Record
          </Button>
        </div>
      ) : null}

      <div className="mt-5">
        {editingProfile ? (
          <div className="rounded-2xl border bg-surface-sunken px-4 pb-2">
            <ProfileForm
              initial={member.profile}
              admin={{ memberId: member.id, name: member.name }}
              submitLabel="Save Record"
              onSubmitted={() => setEditingProfile(false)}
              onCancel={() => setEditingProfile(false)}
            />
          </div>
        ) : (
          <ProfileReview member={member} />
        )}
      </div>

      <div className="mt-5 border-t border-hairline pt-4">
        <h3 className="text-micro text-muted-foreground uppercase">Work</h3>
        <MemberWork memberId={member.id} />
      </div>

      <div className="mt-5 border-t border-hairline pt-4">
        <h3 className="text-micro text-muted-foreground uppercase">Notes</h3>
        <JobNoteComposer entityType="account" entityId={member.id} className="-mx-3 mt-1" />
        <div className="mt-2">
          <JobNotes entityType="account" entityId={member.id} emptyDetail="Notes about this person appear here." />
        </div>
      </div>

      {!isMe ? (
        <div className="mt-5 border-t border-hairline pt-4">
          <h3 className="text-micro text-muted-foreground uppercase">Manage</h3>
          <SettingsRow title="Role" detail="What they may reach in the console.">
            <Segmented<AccountRole>
              label="Role"
              value={member.role}
              onChange={(role) => changeRole.mutate({ id: member.id, role })}
              options={[
                { value: 'staff', label: 'Staff' },
                { value: 'player', label: 'Player' },
                { value: 'admin', label: 'Administrator' },
              ]}
            />
          </SettingsRow>
          {member.role === 'player' ? <SettingsRow title="Level" detail="Where they stand. A level can carry extra money on each month's payment sheet.">
            <Dropdown
              ariaLabel="Level"
              value={member.level?.id ?? ''}
              placeholder="Choose a level"
              menuWidth="compact"
              triggerWidth="auto"
              disabled={setLevel.isPending || !levels.data}
              options={(levels.data ?? []).map((level) => ({ value: level.id, label: level.name }))}
              onChange={(levelId) => {
                const level = levels.data?.find((entry) => entry.id === levelId)
                if (level && levelId !== member.level?.id) setLevel.mutate({ id: member.id, levelId, name: level.name })
              }}
            />
          </SettingsRow> : null}
          <AccessRows member={member} verbs={{ approve, reject, suspend, revoke, restore }} />
          <SettingsRow
            title="Remove from the studio"
            detail="Deletes the account itself — sign-ins, keys, crew rows, the payment records that name them. Payments they recorded are re-attributed to you. This cannot be undone."
          >
            <ConfirmButton
              danger
              label="Delete"
              ask={{
                title: `Delete ${personName(member)}?`,
                detail: 'Their account, sign-ins, keys and crew rows go. This cannot be undone.',
                confirmLabel: 'Delete',
              }}
              busy={removeMember.isPending}
              onConfirm={() => removeMember.mutate(member.id, { onSuccess: () => onClose() })}
            />
          </SettingsRow>
          {fault ? <p className="mt-1 text-caption text-danger">{describeError(fault)}</p> : null}
        </div>
      ) : null}
    </PersonFrame>
  )
}

/** The floating card a person arrives in: the record, scrolling, and a quiet close. */
function PersonFrame({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="relative flex max-h-[min(86vh,44rem)] flex-col overflow-hidden rounded-3xl border bg-surface shadow-modal">
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 sm:px-8 sm:py-6">{children}</div>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-4 top-4 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors duration-fast hover:bg-tint hover:text-foreground"
      >
        <Icons.close />
      </button>
    </div>
  )
}

/**
 * The account's own verbs, one decision per row. Nothing here is reachable
 * from the ledger itself — a status change is a reading of the record, and
 * the record is open on the screen when it happens.
 */
function AccessRows({
  member,
  verbs,
}: {
  member: Account
  verbs: {
    approve: ReturnType<typeof useApproveMember>
    reject: ReturnType<typeof useRejectMember>
    suspend: ReturnType<typeof useSuspendMember>
    revoke: ReturnType<typeof useRevokeMember>
    restore: ReturnType<typeof useRestoreMember>
  }
}) {
  const busy = (key: keyof typeof verbs) => verbs[key].isPending

  switch (member.status) {
    case 'pending':
      return (
        <>
          <SettingsRow title="Decision" detail="Their request is waiting for a yes or a no.">
            <span className="flex items-center gap-1.5">
              <Button size="sm" variant="primary" busy={busy('approve')} onClick={() => verbs.approve.mutate(member.id)}>
                Approve
              </Button>
              <Button size="sm" variant="danger" busy={busy('reject')} onClick={() => verbs.reject.mutate(member.id)}>
                Reject
              </Button>
            </span>
          </SettingsRow>
          <SettingsRow title="Revoke" detail="Take the invitation back before they arrive.">
            <ConfirmButton
              danger
              label="Revoke"
              confirmLabel="Confirm Revoke"
              busy={busy('revoke')}
              onConfirm={() => verbs.revoke.mutate(member.id)}
            />
          </SettingsRow>
        </>
      )
    case 'active':
      return (
        <>
          <SettingsRow title="Suspend access" detail="They keep their history, but cannot sign in.">
            <Button size="sm" variant="danger" busy={busy('suspend')} onClick={() => verbs.suspend.mutate(member.id)}>
              Suspend
            </Button>
          </SettingsRow>
          <SettingsRow title="Revoke membership" detail="Removes them from the studio. This cannot be undone.">
            <ConfirmButton
              danger
              label="Revoke"
              confirmLabel="Confirm Revoke"
              busy={busy('revoke')}
              onConfirm={() => verbs.revoke.mutate(member.id)}
            />
          </SettingsRow>
        </>
      )
    case 'suspended':
      return (
        <>
          <SettingsRow title="Restore access" detail="Puts them back to work as they were.">
            <Button size="sm" busy={busy('restore')} onClick={() => verbs.restore.mutate(member.id)}>
              Restore
            </Button>
          </SettingsRow>
          <SettingsRow title="Revoke membership" detail="Removes them from the studio. This cannot be undone.">
            <ConfirmButton
              danger
              label="Revoke"
              confirmLabel="Confirm Revoke"
              busy={busy('revoke')}
              onConfirm={() => verbs.revoke.mutate(member.id)}
            />
          </SettingsRow>
        </>
      )
    case 'revoked':
    case 'rejected':
      return (
        <SettingsRow title="Restore access" detail="Bring the account back the way it was.">
          <Button size="sm" busy={busy('restore')} onClick={() => verbs.restore.mutate(member.id)}>
            Restore
          </Button>
        </SettingsRow>
      )
    case 'invited':
      return (
        <SettingsRow title="Revoke" detail="Take the invitation back before they arrive.">
          <ConfirmButton
            danger
            label="Revoke"
            confirmLabel="Confirm Revoke"
            busy={busy('revoke')}
            onConfirm={() => verbs.revoke.mutate(member.id)}
          />
        </SettingsRow>
      )
  }
}
